
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "@/hooks/use-toast";

interface PdfDownloaderProps {
  type: "recipe" | "groceryList";
  title: string;
  content: string[] | string;
  additionalInfo?: string;
}

export function PdfDownloader({ type, title, content, additionalInfo }: PdfDownloaderProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePdf = async () => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      const lineHeight = 10;
      let verticalPosition = 20;
      
      // Add title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(title, 20, verticalPosition);
      verticalPosition += lineHeight * 1.5;
      
      // Add content
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      
      if (type === "recipe") {
        // Display instructions as paragraphs
        if (typeof content === "string") {
          const splitText = doc.splitTextToSize(content, 170);
          doc.text(splitText, 20, verticalPosition);
          verticalPosition += splitText.length * 7;
        } else {
          // Add ingredients section
          doc.setFont("helvetica", "bold");
          doc.text("Ingredients:", 20, verticalPosition);
          verticalPosition += lineHeight;
          
          doc.setFont("helvetica", "normal");
          for (const item of content) {
            if (verticalPosition > 270) {
              doc.addPage();
              verticalPosition = 20;
            }
            doc.text(`• ${item}`, 20, verticalPosition);
            verticalPosition += lineHeight;
          }
          
          // Add instructions if available
          if (additionalInfo) {
            verticalPosition += lineHeight;
            doc.setFont("helvetica", "bold");
            doc.text("Instructions:", 20, verticalPosition);
            verticalPosition += lineHeight;
            
            doc.setFont("helvetica", "normal");
            const splitText = doc.splitTextToSize(additionalInfo, 170);
            doc.text(splitText, 20, verticalPosition);
          }
        }
      } else if (type === "groceryList") {
        // Display grocery list items
        if (Array.isArray(content)) {
          for (const item of content) {
            if (verticalPosition > 270) {
              doc.addPage();
              verticalPosition = 20;
            }
            doc.text(`• ${item}`, 20, verticalPosition);
            verticalPosition += lineHeight;
          }
        }
      }
      
      // Add a timestamp and footer
      const timestamp = new Date().toLocaleDateString();
      doc.setFontSize(10);
      doc.text(`Generated on ${timestamp} by Vita`, 20, 285);
      
      // Save the PDF
      const fileName = `${type === "recipe" ? "Recipe" : "Grocery-List"}-${title.replace(/\s+/g, "-")}.pdf`;
      doc.save(fileName);
      
      toast({
        title: "Download Complete",
        description: `Your ${type === "recipe" ? "recipe" : "grocery list"} has been saved as a PDF`,
        type: "success",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Download Failed",
        description: "Could not generate PDF. Please try again.",
        type: "error",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={generatePdf} 
      disabled={isGenerating}
      className="flex items-center gap-1"
    >
      <Download className="h-4 w-4" />
      {isGenerating ? "Generating..." : `Download ${type === "recipe" ? "Recipe" : "Grocery List"}`}
    </Button>
  );
}
